import React from 'react'

import { DATA_TRANSLATION_ATTRIBUTE } from '../constants'
import { sanitizeHtml } from './sanitizeHtml'
import { TranslationInterface } from './types'

// INSPIRED BY https://github.com/lukeed/rosetta

/** Draft wraps everything in paragraphs, so when we need a piece of html without paragraphs, we use InlineHTML */
const removeP = (string_?: string) => string_?.replace(/(^<p>)|(<\/p>$)/g, '')

/** Clean up target and set target="_blank" on external links */
// eslint-disable-next-line sonarjs/slow-regex -- TODO: check slow regexp
const clear = (string_?: string) => string_?.replace(/target="\w+"/g, '').replace(/(href="\S*\/\/\S+")/g, '$1 target="_blank"')

const splitByNode = (part: React.ReactNode, placeholder: string, node: React.ReactNode): React.ReactNode[] =>
  typeof part === 'string' ? part.split(placeholder).flatMap((chunk, index) => (index === 0 ? [chunk] : [node, chunk])) : [part]

const replaceByNodesMap = (string_?: string, replaceMap?: Record<string, React.ReactNode>) =>
  Object.entries(replaceMap || {}).reduce<React.ReactNode[]>(
    (accumulator, [placeholder, node]) => accumulator.flatMap((part) => splitByNode(part, placeholder, node)),
    [string_ || ''],
  )

export const translationHandler = (object?: Record<string, unknown>): TranslationInterface => {
  let locale = ''
  const tree = (object || {}) as Record<string, unknown>

  /** Condition variable to render translation keys as values */
  let keysAsValues: boolean | undefined = false

  const replaceByMap = (string?: string, replaceMap?: Record<string, number | string>) =>
    Object.entries(replaceMap || {}).reduce((accumulator, [key, value]) => accumulator?.replace(key, `${value}`), string || '')

  return {
    blockHTML(key, { preprocess, replace, useDataTranslationAttribute = true, ...restProperties } = {}) {
      const replaced = replace ? replaceByMap(this.t(key), replace) : this.t(key)
      const cleared = clear(replaced)
      const processed = preprocess ? preprocess(cleared) : cleared

      return (
        <div
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(processed) }}
          data-translation={useDataTranslationAttribute ? DATA_TRANSLATION_ATTRIBUTE : undefined}
          {...restProperties}
        />
      )
    },

    inlineHTML(key, { preprocess, replace, shouldReplaceTarget = true, useDataTranslationAttribute = true, ...restProperties } = {}) {
      const replaced = replace ? replaceByMap(this.t(key), replace) : this.t(key)
      const cleared = shouldReplaceTarget ? clear(removeP(replaced)) : removeP(replaced)
      const processed = preprocess ? preprocess(cleared) : cleared

      return (
        <span
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(processed) }}
          data-translation={useDataTranslationAttribute ? DATA_TRANSLATION_ATTRIBUTE : undefined}
          {...restProperties}
        />
      )
    },

    locale(lang?: string) {
      locale = lang || locale

      return locale
    },

    replace(key: string, replace) {
      return replace ? replaceByMap(this.t(key), replace) : this.t(key)
    },

    replaceNodes(key: string, replace) {
      const parts = replaceByNodesMap(this.t(key), replace)

      return (
        <>
          {parts.map((part, index) => (
            <React.Fragment key={`${key}-${index}`}>{part}</React.Fragment>
          ))}
        </>
      )
    },
    set(lang: string, table: Record<string, unknown>) {
      tree[lang] = Object.assign((tree[lang] || {}) as Record<string, unknown>, table)
    },
    setKeysAsValues(isKeysAsValue) {
      keysAsValues = isKeysAsValue
    },
    t(key, parameters, lang) {
      if (keysAsValues) {
        return key
      }

      const value = (tree[lang || locale] as Record<string, unknown>)?.[key]

      if (typeof value === 'function') {
        return value(parameters)
      }

      return value || key
    },
    table(lang: string) {
      return tree[lang] as Record<string, string> | void
    },
  }
}
